import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { EMPTY, Observable, Subject, combineLatest, merge, timer } from 'rxjs';
import { catchError, map, share, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

export type PriceRequestKind = 'handler' | 'priceList';

interface HandlerRecord {
  id: number;
  handler_name: string;
  airport_name: string | null;
  airport_icao: string | null;
  price_request_status: string;
  price_validity: string | null;
  last_request_sent: string | null;
  price_request_emails: string[];
}

interface PriceListRecord {
  id: number;
  airport_data: { id: number; name: string; icao: string; iata: string };
  price_request_status: string;
  price_validity: string | null;
  last_request_sent: string | null;
  price_request_emails: string[];
}

export type PriceRequestRecord = HandlerRecord | PriceListRecord;

export interface PriceRequestSendResult {
  id: number;
  ok: boolean;
  error: string | null;
}

export interface PriceRequestPort {
  load(kind: PriceRequestKind): Observable<PriceRequestRecord[]>;
  send(kind: PriceRequestKind, ids: number[]): Observable<PriceRequestSendResult[]>;
}

export const PRICE_REQUEST_PORT = new InjectionToken<PriceRequestPort>('PriceRequestPort', {
  providedIn: 'root',
  factory: () => inject(PriceRequestHttpAdapter),
});

@Injectable({ providedIn: 'root' })
export class PriceRequestHttpAdapter implements PriceRequestPort {
  private readonly http = inject(HttpClient);
  private readonly listEndpoints: Record<PriceRequestKind, string> = {
    handler: 'handler-admin/needs-update/',
    priceList: 'price-lists/needs-update/',
  };
  private readonly sendEndpoints: Record<PriceRequestKind, string> = {
    handler: 'handler-admin/bulk-send-handler-request/',
    priceList: 'price-lists/bulk-send-price-request/',
  };

  load(kind: PriceRequestKind): Observable<PriceRequestRecord[]> {
    return this.http.get<PriceRequestRecord[]>(
      `${environment.API_ROOT}${this.listEndpoints[kind]}`,
    );
  }

  send(kind: PriceRequestKind, ids: number[]): Observable<PriceRequestSendResult[]> {
    return this.http.post<{ results: PriceRequestSendResult[] }>(
      `${environment.API_ROOT}${this.sendEndpoints[kind]}`,
      { ids },
    ).pipe(map(response => response.results));
  }
}

export interface PriceRequestCandidate {
  id: number;
  name: string;
  airportName: string | null;
  airportIcao: string | null;
  statusLabel: string;
  statusClass: string;
  priceValidity: string | null;
  lastRequestSent: string | null;
  recipientEmails: string[];
}

export interface PriceRequestGroup {
  kind: PriceRequestKind;
  title: string;
  description: string;
  icon: string;
  itemLabel: string;
  candidates: PriceRequestCandidate[];
}

export interface PriceRequestSendSummary {
  sent: number;
  failed: PriceRequestSendResult[];
}

@Injectable({ providedIn: 'root' })
export class PriceRequestInbox {
  private readonly port = inject(PRICE_REQUEST_PORT);
  private readonly manualRefresh$ = new Subject<void>();
  private readonly refreshRequests$ = merge(
    timer(0, 5 * 60 * 1000),
    this.manualRefresh$,
  ).pipe(share());

  readonly groups$: Observable<PriceRequestGroup[]> = combineLatest([
    this.loadGroup('priceList'),
    this.loadGroup('handler'),
  ]).pipe(
    map(groups => groups.filter((group): group is PriceRequestGroup => group !== null)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly totalCount$ = this.groups$.pipe(
    map(groups => groups.reduce((total, group) => total + group.candidates.length, 0)),
  );

  refresh(): void {
    this.manualRefresh$.next();
  }

  send(kind: PriceRequestKind, ids: number[]): Observable<PriceRequestSendSummary> {
    return this.port.send(kind, ids).pipe(
      map(results => ({
        sent: results.filter(result => result.ok).length,
        failed: results.filter(result => !result.ok),
      })),
      tap(() => this.refresh()),
    );
  }

  private loadGroup(kind: PriceRequestKind): Observable<PriceRequestGroup | null> {
    return this.refreshRequests$.pipe(
      switchMap(() => this.port.load(kind).pipe(catchError(() => EMPTY))),
      map(records => this.group(kind, records)),
      startWith(null),
    );
  }

  private group(kind: PriceRequestKind, records: PriceRequestRecord[]): PriceRequestGroup {
    const priceList = kind === 'priceList';
    return {
      kind,
      title: priceList ? 'Price lists need update' : 'Handlers need update',
      description: priceList
        ? 'price list(s) require a refresh request'
        : 'handler(s) require a refresh request',
      icon: priceList ? 'fas fa-file-invoice-dollar' : 'fas fa-handshake',
      itemLabel: priceList ? 'price list' : 'handler',
      candidates: records.map(record => this.normalize(kind, record)),
    };
  }

  private normalize(kind: PriceRequestKind, record: PriceRequestRecord): PriceRequestCandidate {
    const status = record.price_request_status;
    const priceList = kind === 'priceList' ? record as PriceListRecord : null;
    const handler = kind === 'handler' ? record as HandlerRecord : null;
    return {
      id: record.id,
      name: priceList?.airport_data.icao ?? handler?.handler_name ?? '',
      airportName: priceList?.airport_data.name ?? handler?.airport_name ?? null,
      airportIcao: priceList?.airport_data.icao ?? handler?.airport_icao ?? null,
      statusLabel: this.statusLabel(status),
      statusClass: this.statusClass(status),
      priceValidity: record.price_validity,
      lastRequestSent: record.last_request_sent,
      recipientEmails: record.price_request_emails ?? [],
    };
  }

  private statusLabel(status: string): string {
    const labels: Record<string, string> = {
      EXPIRING_SOON: 'Expiring soon',
      EXPIRED: 'Expired',
      REQUEST_ERROR: 'Request error',
    };
    return labels[status] ?? status;
  }

  private statusClass(status: string): string {
    const classes: Record<string, string> = {
      EXPIRED: 'badge-expired',
      EXPIRING_SOON: 'badge-expiring',
      REQUEST_ERROR: 'badge-error',
    };
    return classes[status] ?? '';
  }
}
